import { useMemo } from 'react';
import type { ControllerFieldState } from 'react-hook-form';
import { type SxProps, type Theme,useTheme } from '@mui/material';

export type ErrorSeverity = 'error' | 'warning' | 'info' | null;

interface FieldErrorStyling {
  /** The severity level extracted from error type */
  severity: ErrorSeverity;
  /** Whether to show error state (only for actual errors, not warnings/info) */
  error: boolean;
  /** SxProps to apply to the TextField for styling */
  sx: SxProps<Theme>;
  /** The color to use for helper text */
  helperTextColor?: string;
}

/**
 * Hook to determine field error styling based on error type
 * 
 * Extracts severity from error types like:
 * - 'scrubber-error' -> error severity (red)
 * - 'scrubber-warning' -> warning severity (orange)
 * - 'scrubber-info' -> info severity (blue)
 * 
 * @param fieldState - The field state from react-hook-form Controller
 * @returns Styling props to apply to TextField
 */
export const useFieldErrorStyling = (
  fieldState?: ControllerFieldState
): FieldErrorStyling => {
  const theme = useTheme();
  
  return useMemo(() => {
    // No error state
    if (!fieldState?.error) {
      return {
        severity: null,
        error: false,
        sx: {},
      };
    }

    // Extract severity from error type
    const errorType = fieldState.error.type;
    let severity: ErrorSeverity = 'error'; // Default to error if no type

    if (typeof errorType === 'string') {
      if (errorType === 'scrubber-warning' || errorType.includes('warning')) {
        severity = 'warning';
      } else if (errorType === 'scrubber-info' || errorType.includes('info')) {
        severity = 'info';
      } else if (errorType === 'scrubber-error' || errorType.includes('error')) {
        severity = 'error';
      }
    }

    // Define styles based on severity using theme colors
    const severityStyles: Record<NonNullable<ErrorSeverity>, SxProps<Theme>> = {
      error: {
        '& .MuiOutlinedInput-root': {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.error.main,
            borderWidth: 2,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.error.main,
            borderWidth: 2,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.error.main,
            borderWidth: 2,
          },
        },
        '& .MuiFormHelperText-root': {
          color: theme.palette.error.main,
          fontWeight: 500,
        },
      },
      warning: {
        '& .MuiOutlinedInput-root': {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.warning.main,
            borderWidth: 2,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.warning.main,
            borderWidth: 2,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.warning.main,
            borderWidth: 2,
          },
        },
        '& .MuiFormHelperText-root': {
          color: theme.palette.warning.main,
          fontWeight: 500,
        },
      },
      info: {
        '& .MuiOutlinedInput-root': {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.info.main,
            borderWidth: 1.5,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.info.main,
            borderWidth: 1.5,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.info.main,
            borderWidth: 1.5,
          },
        },
        '& .MuiFormHelperText-root': {
          color: theme.palette.info.main,
          fontWeight: 400,
        },
      },
    };

    const getHelperTextColor = () => {
      switch (severity) {
        case 'error':
          return theme.palette.error.main;
        case 'warning':
          return theme.palette.warning.main;
        case 'info':
          return theme.palette.info.main;
        default:
          return theme.palette.text.secondary;
      }
    };

    return {
      severity,
      error: severity === 'error', // Only show MUI error state for actual errors
      sx: severityStyles[severity],
      helperTextColor: getHelperTextColor(),
    };
  }, [fieldState?.error, theme.palette.error.main, theme.palette.warning.main, theme.palette.info.main, theme.palette.text.secondary]);
};

/**
 * Hook to get styles for RadioGroup/Checkbox components with FormHelperText
 * Since these don't have borders, we only style the helper text
 */
export const useFieldErrorHelperTextStyling = (
  fieldState?: ControllerFieldState
): { severity: ErrorSeverity; helperTextSx: SxProps<Theme> } => {
  const theme = useTheme();
  
  return useMemo(() => {
    if (!fieldState?.error) {
      return {
        severity: null,
        helperTextSx: {},
      };
    }

    const errorType = fieldState.error.type;
    let severity: ErrorSeverity = 'error';

    if (typeof errorType === 'string') {
      if (errorType === 'scrubber-warning' || errorType.includes('warning')) {
        severity = 'warning';
      } else if (errorType === 'scrubber-info' || errorType.includes('info')) {
        severity = 'info';
      } else if (errorType === 'scrubber-error' || errorType.includes('error')) {
        severity = 'error';
      }
    }

    const helperTextStyles: Record<NonNullable<ErrorSeverity>, SxProps<Theme>> = {
      error: {
        color: theme.palette.error.main,
        fontWeight: 500,
        fontSize: '11px',
        marginTop: '4px',
      },
      warning: {
        color: theme.palette.warning.main,
        fontWeight: 500,
        fontSize: '11px',
        marginTop: '4px',
      },
      info: {
        color: theme.palette.info.main,
        fontWeight: 400,
        fontSize: '11px',
        marginTop: '4px',
      },
    };

    return {
      severity,
      helperTextSx: helperTextStyles[severity],
    };
  }, [fieldState?.error, theme.palette.error.main, theme.palette.warning.main, theme.palette.info.main]);
};